import { fetchMethods, getPaymentMethods } from "../utils/api";
import { useState, useEffect } from "react";
import { currencyMeta } from "../utils/currency";
import { MethodsType, CurrencyMetaType } from "../utils/types";

/**
 * Справочник методов и валют для выпадающих списков.
 *
 * Основной источник — реестр /payment_methods/: методы заводятся из админки и
 * должны появляться в списках без выката фронта. Прежняя ручка
 * /transactions/available_methods/ отдаёт статический перечень из кода и о
 * новых методах не знает.
 *
 * Реестр доступен только роли admin, поэтому для support остаётся откат на
 * старую ручку — иначе у поддержки выпадающие списки опустели бы совсем.
 */
export const useMethods = () => {
  const [methods, setMethods] = useState<MethodsType>({});
  const [currency, setCurrency] = useState<CurrencyMetaType>({});

  const toCurrencyMeta = (codes: string[]): CurrencyMetaType =>
    codes.reduce((acc, value) => {
      const key = value.toUpperCase();
      acc[key] = currencyMeta[key] ?? {
        label: value,
        img: "/images/unknown.png",
      };
      return acc;
    }, {} as CurrencyMetaType);

  const getMethods = async () => {
    try {
      const res = await getPaymentMethods();
      const items: { code: string }[] = res.data?.items ?? [];
      setMethods(
        items.reduce((acc, item) => {
          acc[item.code] = item.code;
          return acc;
        }, {} as MethodsType),
      );
      setCurrency(toCurrencyMeta(res.data?.currencies ?? []));
      return;
    } catch (err) {
      // 403 у роли support — ожидаемо, идём на старую ручку. Прочие ошибки
      // тоже не должны оставлять экран без справочника.
      console.warn("Реестр методов недоступен, берём статический список", err);
    }

    try {
      const res = await fetchMethods();
      setMethods(res.data["payment_methods"]);
      const backendCurrencies: Record<string, string> =
        res.data["currencies"] ?? {};
      setCurrency(toCurrencyMeta(Object.values(backendCurrencies)));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getMethods();
  }, []);

  return {
    methods,
    currency,
  };
};
